import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Pane from "./Pane";

interface SplitterLayoutProps {
    customClassName?: string;
    vertical?: boolean;
    percentage?: boolean;
    primaryIndex?: number;
    primaryMinSize?: number;
    secondaryInitialSize?: number;
    secondaryMinSize?: number;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    onSecondaryPaneSizeChange?: (size: number) => void;
    children: React.ReactNode[];
}

function clearSelection(): void {
    if ((document.body as any).createTextRange) {
        const range = (document.body as any).createTextRange();
        range.collapse();
        range.select();
    } else if (window.getSelection) {
        const selection = window.getSelection();
        if (selection?.empty) {
            selection.empty();
        } else if (selection?.removeAllRanges) {
            selection.removeAllRanges();
        }
    } else if ((document as any).selection) {
        (document as any).selection.empty();
    }
}

const DEFAULT_SPLITTER_SIZE = 4;

const SplitterLayout: React.FC<SplitterLayoutProps> = ({
    customClassName = "",
    vertical = false,
    percentage = false,
    primaryIndex = 0,
    primaryMinSize = 0,
    secondaryInitialSize,
    secondaryMinSize = 0,
    onDragStart = null,
    onDragEnd = null,
    onSecondaryPaneSizeChange = null,
    children,
}) => {
    const [secondaryPaneSize, setSecondaryPaneSize] = useState<number>(
        secondaryInitialSize ?? 0
    );
    const [resizing, setResizing] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const splitterRef = useRef<HTMLDivElement>(null);
    const [containerClasses, setContainerClasses] = useState("splitter-layout");

    const handleSplitterMouseDown = () => {
        clearSelection();
        setResizing(true);
    };

    const getSecondaryPaneSize = useCallback(
        (
            containerRect: DOMRect,
            splitterRect: DOMRect,
            clientPosition: { left: number; top: number },
            offsetMouse: boolean
        ): number => {
            let totalSize: number;
            let splitterSize: number;
            let offset: number;
            if (vertical) {
                totalSize = containerRect.height;
                splitterSize = splitterRect.height;
                offset = clientPosition.top - containerRect.top;
            } else {
                totalSize = containerRect.width;
                splitterSize = splitterRect.width;
                offset = clientPosition.left - containerRect.left;
            }
            if (offsetMouse) {
                offset -= splitterSize / 2;
            }
            if (offset < 0) {
                offset = 0;
            } else if (offset > totalSize - splitterSize) {
                offset = totalSize - splitterSize;
            }

            let newSecondaryPaneSize: number;
            if (primaryIndex === 1) {
                newSecondaryPaneSize = offset;
            } else {
                newSecondaryPaneSize = totalSize - splitterSize - offset;
            }
            let primaryPaneSize =
                totalSize - splitterSize - newSecondaryPaneSize;
            if (percentage) {
                newSecondaryPaneSize = (newSecondaryPaneSize * 100) / totalSize;
                primaryPaneSize = (primaryPaneSize * 100) / totalSize;
                splitterSize = (splitterSize * 100) / totalSize;
                totalSize = 100;
            }

            if (primaryPaneSize < primaryMinSize) {
                newSecondaryPaneSize = Math.max(
                    newSecondaryPaneSize - (primaryMinSize - primaryPaneSize),
                    0
                );
            } else if (newSecondaryPaneSize < secondaryMinSize) {
                newSecondaryPaneSize = Math.min(
                    totalSize - splitterSize - primaryMinSize,
                    secondaryMinSize
                );
            }

            return newSecondaryPaneSize;
        },
        [percentage, primaryIndex, primaryMinSize, secondaryMinSize, vertical]
    );

    useEffect(() => {
        const handleResize = () => {
            if (splitterRef.current && !percentage) {
                const containerRect =
                    containerRef.current?.getBoundingClientRect();
                const splitterRect =
                    splitterRef.current.getBoundingClientRect();
                if (containerRect) {
                    const newSecondaryPaneSize = getSecondaryPaneSize(
                        containerRect,
                        splitterRect,
                        {
                            left: splitterRect.left ?? DEFAULT_SPLITTER_SIZE,
                            top: splitterRect.top ?? DEFAULT_SPLITTER_SIZE,
                        },
                        false
                    );
                    setSecondaryPaneSize(newSecondaryPaneSize);
                }
            }
        };

        const handleMouseUp = () => {
            setResizing(false);
        };

        const handleMouseMove = (e: MouseEvent | Touch) => {
            if (resizing && containerRef.current && splitterRef.current) {
                const containerRect =
                    containerRef.current.getBoundingClientRect();
                const splitterRect =
                    splitterRef.current.getBoundingClientRect();
                const newSecondaryPaneSize = getSecondaryPaneSize(
                    containerRect,
                    splitterRect,
                    { left: e.clientX, top: e.clientY },
                    true
                );
                clearSelection();
                setSecondaryPaneSize(newSecondaryPaneSize);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            handleMouseMove(e.changedTouches[0]);
        };

        handleResize();

        window.addEventListener("resize", handleResize);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("touchend", handleMouseUp);
        document.addEventListener("touchmove", handleTouchMove);

        return () => {
            window.removeEventListener("resize", handleResize);
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("touchend", handleMouseUp);
            document.removeEventListener("touchmove", handleTouchMove);
        };
    }, [resizing, percentage, getSecondaryPaneSize]);

    useEffect(() => {
        if (onSecondaryPaneSizeChange) {
            onSecondaryPaneSizeChange(secondaryPaneSize);
        }
    }, [onSecondaryPaneSizeChange, secondaryPaneSize]);

    useEffect(() => {
        if (resizing && onDragStart) {
            onDragStart();
        } else if (!resizing && onDragEnd) {
            onDragEnd();
        }
    }, [onDragEnd, onDragStart, resizing]);

    useEffect(() => {
        let containerClasses = "splitter-layout";
        if (customClassName) {
            containerClasses += ` ${customClassName}`;
        }
        if (vertical) {
            containerClasses += " splitter-layout-vertical";
        }
        if (resizing) {
            containerClasses += " layout-changing";
        }
        setContainerClasses(containerClasses);
    }, [customClassName, resizing, vertical]);

    const wrappedChildren = useMemo(() => {
        const childArray = React.Children.toArray(children).slice(0, 2);
        if (childArray.length === 0) {
            childArray.push(<div />);
        }
        const effectivePrimaryIndex =
            primaryIndex !== 0 && primaryIndex !== 1 ? 0 : primaryIndex;
        return childArray.map((child, i) => {
            const primary =
                childArray.length <= 1 || i === effectivePrimaryIndex;
            const size = primary ? null : secondaryPaneSize;
            return (
                <Pane
                    vertical={vertical}
                    percentage={percentage}
                    primary={primary}
                    size={size}
                >
                    {child}
                </Pane>
            );
        });
    }, [children, primaryIndex, secondaryPaneSize, vertical, percentage]);

    return (
        <div className={containerClasses} ref={containerRef}>
            {wrappedChildren[0]}
            {wrappedChildren.length > 1 && (
                <div
                    role="separator"
                    className="layout-splitter"
                    ref={splitterRef}
                    onMouseDown={handleSplitterMouseDown}
                    onTouchStart={handleSplitterMouseDown}
                />
            )}
            {wrappedChildren.length > 1 && wrappedChildren[1]}
        </div>
    );
};

export default SplitterLayout;
